// Time-ledger commands: add / edit / delete / move a logged entry. Each change lands WITH its
// ledger record (timeEdits, timeledger.js) in one group, so totals and history can't disagree.
// task.timeSpent moves by the DURATION DELTA — never recomputed from the array (older tasks carry
// timeSpent that predates timeEntries). Pure.
import { timeEditRecord } from '../timeledger.js';
import { set, update } from './writes.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const need = (ctx, id) => { const t = (ctx.tasks || []).find((x) => x.id === id); if (!t) throw new Error(`no task ${id}`); return t; };
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^\d{1,2}:\d{2}$/;

export function entryDurationMs(startTime, endTime) {
  const toMin = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  let mins = toMin(endTime) - toMin(startTime);
  if (mins < 0) mins += 24 * 60;
  return mins * 60000;
}

function checkEntry(e) {
  if (!DATE.test(e.date || '')) throw new Error('date must be YYYY-MM-DD');
  if (!HHMM.test(e.startTime || '') || !HHMM.test(e.endTime || '')) throw new Error('startTime / endTime must be HH:MM');
}

function commit(ctx, patches, rec) {
  const id = ctx.newId();
  const record = timeEditRecord({ ...rec, id, by: ctx.by || null, now: now(ctx) });
  return { writes: [...patches.map((p) => update(`tasks/${p.id}`, p.data)), set(`timeEdits/${id}`, record)], record };
}

export function addTimeEntry(ctx, { taskId, date, startTime, endTime, note = null, reason = null }) {
  const task = need(ctx, taskId);
  checkEntry({ date, startTime, endTime });
  const duration = entryDurationMs(startTime, endTime);
  if (duration <= 0) throw new Error('the entry has no duration');
  const entry = { id: ctx.newId(), date, startTime, endTime, duration, note: note || null, manual: true, createdAt: now(ctx) };
  const r = commit(ctx, [{ id: taskId, data: { timeEntries: [...(task.timeEntries || []), entry], timeSpent: Math.max(0, (task.timeSpent || 0) + duration) } }], { kind: 'add', task, after: entry, reason });
  return { ...r, entry };
}

export function editTimeEntry(ctx, { taskId, entryId, date, startTime, endTime, note, reason = null }) {
  const task = need(ctx, taskId);
  const entries = task.timeEntries || [];
  const idx = entries.findIndex((e) => e.id === entryId);
  if (idx < 0) throw new Error(`no entry ${entryId} on ${task.title}`);
  const prev = entries[idx];
  const next = { ...prev, ...(date !== undefined ? { date } : {}), ...(startTime !== undefined ? { startTime } : {}), ...(endTime !== undefined ? { endTime } : {}), ...(note !== undefined ? { note } : {}) };
  checkEntry(next);
  next.duration = entryDurationMs(next.startTime, next.endTime);
  const delta = next.duration - (prev.duration || 0);
  const r = commit(ctx, [{ id: taskId, data: { timeEntries: entries.map((e, i) => (i === idx ? next : e)), timeSpent: Math.max(0, (task.timeSpent || 0) + delta) } }], { kind: 'edit', task, before: prev, after: next, reason });
  return { ...r, entry: next, before: prev };
}

export function deleteTimeEntry(ctx, { taskId, entryId, reason = null }) {
  const task = need(ctx, taskId);
  const entries = task.timeEntries || [];
  const entry = entries.find((e) => e.id === entryId);
  if (!entry) throw new Error(`no entry ${entryId} on ${task.title}`);
  const r = commit(ctx, [{ id: taskId, data: { timeEntries: entries.filter((e) => e.id !== entryId), timeSpent: Math.max(0, (task.timeSpent || 0) - (entry.duration || 0)) } }], { kind: 'delete', task, before: entry, reason });
  return { ...r, entry };
}

/** A logged stretch landed on the wrong task: move it (entry + its duration) in ONE write. */
export function moveTimeEntry(ctx, { fromTaskId, entryId, toTaskId, reason = null }) {
  if (fromTaskId === toTaskId) throw new Error('same task');
  const from = need(ctx, fromTaskId); const to = need(ctx, toTaskId);
  const entry = (from.timeEntries || []).find((e) => e.id === entryId);
  if (!entry) throw new Error(`no entry ${entryId} on ${from.title}`);
  const dur = entry.duration || 0;
  const r = commit(ctx, [
    { id: fromTaskId, data: { timeEntries: (from.timeEntries || []).filter((e) => e.id !== entryId), timeSpent: Math.max(0, (from.timeSpent || 0) - dur) } },
    { id: toTaskId, data: { timeEntries: [...(to.timeEntries || []), { ...entry, movedFrom: fromTaskId, movedAt: now(ctx) }], timeSpent: (to.timeSpent || 0) + dur } },
  ], { kind: 'move', task: from, toTask: to, before: entry, after: entry, reason });
  return { ...r, entry };
}

/** The dates a time change touches — the approval rule holds edits to days before today. */
export function timeEditDates(record) {
  return [...new Set([record?.before?.date, record?.after?.date].filter(Boolean))];
}
